import {
    Alert,
    Box,
    FormControlLabel,
    Link,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography
} from '@mui/material'
import GeneralInfoSubmitButton from './GeneralInfoSubmitButton';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import RofTooltip from '../../../../shared-components/RofTooltip';
import { formatFilename, formMaxWidth } from '../constants/general-info-constants';
import EmptySelect from './EmptySelect';

/**
 * This is a modular form field generator. If you pass a formObjectArray as specified, it will automate the creation of the form fields required.
 * @param {Object} props
 * @param {Array<{type: "text" | "select" | "radio" | "file", fieldId: string, value: string | null, text: string, options?: Array<string>, alert?: boolean, tooltip?: string }>} props.formObjectArray Objects which control the type, text and other attributes of the field which will be generated for the form.
 * @param {Object} props.formState Current form state - used to manage file display logic for the end user.
 * @param {Boolean} props.isLoading Loading state to manage the submit button
 * @param {null | string} props.formErrors Renders null or the string held in state for the user.
 * @param {number} props.fileInputKey React workaround to reset file fields. Since you cannot update the value of the file directly, changing the key prop of the file input forces a re-render. Thus, a reset.
 * @param {(event: Event) => void} props.onChange Handles onChange events to update the currently held form state.
 * @param {() => void} props.onSubmit Submit logic for the form.
 * @returns {JSX.Element}
 */
export default function GeneralInfoForm({ formObjectArray, formState, isLoading, formErrors, fileInputKey, onChange, onSubmit }) {

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: formMaxWidth }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {formObjectArray && Array.isArray(formObjectArray) &&
                        formObjectArray.map((formObjectItem, index) => {
                            const { type, fieldId, value, text, options, selectPlaceholder, alert, tooltip, ...restProps } = formObjectItem
                            switch (type) {
                                case 'text':
                                    return <CustomTextField
                                        key={index}
                                        fieldId={fieldId}
                                        value={value}
                                        text={text}
                                        tooltip={tooltip}
                                        alert={alert}
                                        onChange={onChange}
                                        {...restProps}
                                    />
                                case 'select':
                                    return <CustomSelectField
                                        key={index}
                                        fieldId={fieldId}
                                        value={value}
                                        text={text}
                                        options={options}
                                        selectPlaceholder={selectPlaceholder}
                                        tooltip={tooltip}
                                        onChange={onChange}
                                        {...restProps}
                                    />
                                case 'radio':
                                    return <CustomRadioGroupField
                                        key={index}
                                        fieldId={fieldId}
                                        value={value}
                                        text={text}
                                        tooltip={tooltip}
                                        onChange={onChange}
                                        {...restProps}
                                    />
                                case 'file':
                                    return <CustomFileField
                                        key={index}
                                        fileInputKey={fileInputKey}
                                        fieldId={fieldId}
                                        text={text}
                                        fileResponseObject={formState[fieldId]}
                                        tooltip={tooltip}
                                        onChange={onChange}
                                        {...restProps}
                                    />
                                case 'No Supervisor':
                                    return <NoSupervisor key={index} />
                                default:
                                    return null
                            }
                        })
                    }
                    <ErrorMessage formErrors={formErrors} />
                    <GeneralInfoSubmitButton onSubmit={onSubmit} isLoading={isLoading} />
                </Box>
            </Box>
        </Box>
    )
}


function CustomTextField({ fieldId, value, text, onChange, alert, tooltip, ...rest }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <CustomSubHeader text={text} tooltip={tooltip} />
            <TextField
                inputProps={{
                    sx: {
                        '::placeholder': {
                            fontStyle: 'italic',
                        }
                    }
                }}
                sx={{ '& textarea::placeholder': { fontStyle: 'italic' } }}
                name={fieldId}
                value={value ?? ''}
                onChange={onChange}
                fullWidth
                {...rest}
            />
            {
                alert &&
                <FeedstockSourceAlert />
            }
        </Box>
    )

}

function CustomSelectField({ fieldId, value, text, onChange, options, selectPlaceholder, tooltip, ...rest }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <CustomSubHeader text={text} tooltip={tooltip} />
            <Select
                name={fieldId}
                value={value ?? ''}
                onChange={onChange}
                displayEmpty
                renderValue={(selected) => (selected ? selected : EmptySelect({ text: selectPlaceholder }))}
                {...rest}
            >
                {options && Array.isArray(options) &&
                    options.map((option, index) => (
                        <MenuItem key={index} value={option}>
                            {option}
                        </MenuItem>
                    ))}
            </Select>
        </Box>
    )

}

function CustomRadioGroupField({ fieldId, value, text, onChange, tooltip, ...rest }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <CustomSubHeader text={text} tooltip={tooltip} />
            <RadioGroup
                sx={{ pl: 1 }}
                row
                name={fieldId}
                onChange={onChange}
                value={value ?? ''}
                {...rest}
            >
                <FormControlLabel
                    value='yes'
                    control={<Radio />}
                    label='Yes'
                    name={fieldId}
                />
                <FormControlLabel
                    value='no'
                    control={<Radio />}
                    label='No'
                    name={fieldId}
                />
            </RadioGroup>
        </Box>
    )
}


function CustomFileField({ fieldId, text, fileResponseObject, fileInputKey, onChange, tooltip, ...rest }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, justifyContent: 'flex-start' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <CustomSubHeader text={text} tooltip={tooltip} />
                <TextField
                    key={fileInputKey}
                    name={fieldId}
                    type='file'
                    onChange={onChange}
                    fullWidth
                    {...rest}
                />
            </Box>
            {fileResponseObject && fileResponseObject.filename && fileResponseObject.fileurl &&
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left', justifyContent: 'flex-end', gap: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', gap: 1 }}>
                        <DriveFolderUploadOutlinedIcon sx={{ verticalAlign: 'middle' }} />
                        <Typography sx={{ fontWeight: 'bold', fontSize: 18 }}>
                            Currently uploaded file:
                        </Typography>
                    </Box>
                    <Box>
                        <Typography sx={{ fontWeight: 'bold', fontSize: 18 }}>
                            <Link href={fileResponseObject.fileurl} target={'_blank'}>{formatFilename(fileResponseObject.filename)}</Link>
                        </Typography>
                    </Box>
                </Box>
            }
        </Box>
    )
}

function FeedstockSourceAlert() {
    return (
        <Alert severity='warning'>
            Click here to access{" "}
            <Link href='https://what3words.com/' target={'_blank'} sx={{ fontWeight: 'bold' }}>
                what3words.com
            </Link>
            . Please copy and paste the three words from a block corresponding to the
            location where you can find the feedstock on the farm.
        </Alert>
    )
}

function NoSupervisor() {
    return (
        <Alert severity='warning' sx={{ width: 'fit-content' }}>Please ensure your team gets adequate training before conducting the burn - for your safety, and for all involved.</Alert>
    )
}

function ErrorMessage({ formErrors }) {
    return (
        <>
            {
                formErrors
                    ? <Alert sx={{ width: 'fit-content' }} severity='error'>
                        {formErrors}
                    </Alert>
                    : null
            }
        </>
    )
}

function CustomSubHeader({ text, tooltip }) {
    return (
        <Typography sx={{ fontWeight: 'bold' }}>
            {text}
            {tooltip &&
                <RofTooltip tooltipText={tooltip} />
            }
        </Typography>
    )
}